import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-inter text-sm font-medium transition-all duration-150 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 active:scale-98 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-blue-500 text-white shadow hover:bg-blue-600 active:bg-blue-700",
        destructive:
          "bg-red-500 text-white shadow-sm hover:bg-red-600 active:bg-red-700",
        outline:
          "border border-neutral-100 bg-white shadow-sm hover:bg-blue-25 hover:text-blue-500 hover:border-neutral-100 active:bg-blue-50 active:text-blue-500 active:border-neutral-100 data-[state=open]:bg-blue-50 data-[state=open]:text-blue-500 data-[state=open]:border-neutral-100",
        secondary:
          "bg-neutral-100 text-neutral-900 shadow-sm hover:bg-neutral-200",
        ghost:
          "hover:bg-blue-25 hover:text-blue-500 active:bg-blue-50 active:text-blue-500 disabled:bg-transparent data-[state=open]:bg-blue-50 data-[state=open]:text-blue-500",
        link: "text-blue-500 underline-offset-4 hover:underline hover:text-blue-600 active:text-blue-700 disabled:bg-transparent",
        // Sked custom variants
        "blue-primary":
          "bg-blue-500 text-white shadow hover:bg-blue-600 active:bg-blue-700 disabled:bg-blue-200",
        "blue-secondary":
          "bg-blue-50 text-blue-600 shadow-sm hover:bg-blue-100 border border-blue-100",
        "red-primary":
          "bg-red-500 text-white shadow hover:bg-red-600 active:bg-red-700 disabled:bg-red-200",
        "red-secondary":
          "border border-red-100 bg-transparent text-red-600 shadow-sm hover:bg-red-25 active:bg-red-50 disabled:bg-transparent disabled:text-neutral-800 disabled:border-neutral-100",
        "destructive-secondary":
          "border border-red-100 bg-transparent text-red-600 shadow-sm hover:bg-red-25 active:bg-red-50 disabled:bg-transparent disabled:text-neutral-800 disabled:border-neutral-100",
        "red-outline":
          "border border-red-500 bg-white text-red-600 shadow-sm hover:bg-red-50",
        "green-primary":
          "bg-green-500 text-white shadow hover:bg-green-600 active:bg-green-700 disabled:bg-green-200",
        success:
          "bg-green-500 text-white shadow hover:bg-green-600 active:bg-green-700 disabled:bg-green-200",
        "green-secondary":
          "border border-green-100 bg-transparent text-green-600 shadow-sm hover:bg-green-25 active:bg-green-50 disabled:bg-transparent disabled:text-neutral-800 disabled:border-neutral-100",
        "success-secondary":
          "border border-green-100 bg-transparent text-green-600 shadow-sm hover:bg-green-25 active:bg-green-50 disabled:bg-transparent disabled:text-neutral-800 disabled:border-neutral-100",
        "orange-primary":
          "bg-orange-500 text-white shadow hover:bg-orange-600 active:bg-orange-700 disabled:bg-orange-200",
        "pink-primary":
          "bg-[#ED207C] text-white shadow hover:bg-[#d91d6e] active:bg-[#b8185c] disabled:bg-[#f5a0c4]",
        "blue-tertiary":
          "bg-transparent text-blue-600 hover:bg-blue-50 disabled:text-blue-200",
        "blurple-secondary":
          "border border-blue-500 bg-white text-blue-600 shadow-sm hover:bg-blue-50",
        "black-secondary":
          "border border-neutral-100 bg-white text-neutral-700 shadow-sm hover:bg-neutral-50",
        "black-tertiary":
          "bg-transparent text-neutral-900 shadow-none hover:bg-neutral-50 active:bg-neutral-100 data-[state=open]:bg-neutral-50",
        "black-primary":
          "bg-neutral-900 text-white shadow hover:bg-neutral-800 active:bg-neutral-900 disabled:bg-neutral-400",
      },
      size: {
        default: "h-9 px-4 py-2",
        xs: "h-7 rounded-md px-2 text-xs",
        sm: "h-8 rounded-md px-3 py-1.5",
        md: "h-9 px-4 py-2",
        lg: "h-10 rounded-md px-8",
        icon: "h-10 w-10",
        "icon-xs": "size-7 p-0",
        "icon-sm": "size-8 p-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
